'use client';

import {Dialog as DialogPrimitive} from '@base-ui/react/dialog';
import {useDirection} from '@base-ui/react/direction-provider';
import {XIcon} from 'lucide-react';
import {createContext, useContext, useRef} from 'react';

import {Button} from './button';
import {cn} from '../../lib/utils';

import type {ComponentProps, RefObject} from 'react';

type DialogOutsidePress = 'nudge' | 'close';

const DialogContext = createContext<{
  popupRef: RefObject<HTMLDivElement | null>;
  isNested: boolean;
} | null>(null);

function Dialog({
  onOpenChange,
  outsidePress = 'nudge',
  dismissible = true,
  ...props
}: DialogPrimitive.Root.Props & {outsidePress?: DialogOutsidePress; dismissible?: boolean}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const isNested = useContext(DialogContext) !== null;

  const handleOpenChange: DialogPrimitive.Root.Props['onOpenChange'] = (open, eventDetails) => {
    const isNudged =
      !dismissible || (outsidePress === 'nudge' && eventDetails.reason === 'outside-press');
    if (!open && isNudged) {
      eventDetails.cancel();
      nudgePopup(popupRef.current);
      return;
    }
    onOpenChange?.(open, eventDetails);
  };

  return (
    <DialogContext.Provider value={{popupRef, isNested}}>
      <DialogPrimitive.Root data-slot="dialog" onOpenChange={handleOpenChange} {...props} />
    </DialogContext.Provider>
  );
}

// Web Animations API layers on top of the CSS enter animation instead of replacing it, which would restart the fade-in.
function nudgePopup(popup: HTMLElement | null) {
  if (!popup || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  popup.animate([{scale: '1'}, {scale: '1.02'}, {scale: '1'}], {duration: 200, easing: 'ease-out'});
}

function DialogTrigger({...props}: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({container, ...props}: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" container={container} {...props} />;
}

function DialogClose({...props}: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({className, ...props}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 bg-background-overlay/70 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  container,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  container?: DialogPrimitive.Portal.Props['container'];
}) {
  const direction = useDirection();
  const dialog = useContext(DialogContext);
  return (
    <DialogPortal container={container}>
      {/* The parent dialog's backdrop is already showing, so a nested one only needs a light tint. */}
      <DialogOverlay forceRender className={cn(dialog?.isNested && 'bg-background-overlay/35')} />
      <DialogPrimitive.Popup
        ref={dialog?.popupRef}
        data-slot="dialog-content"
        dir={direction}
        className={cn(
          'fixed top-1/2 inset-s-1/2 z-50 flex flex-col w-[calc(100vw-48px)] max-h-[85vh] ltr:-translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 gap-8 rounded-2xl bg-background-surface overflow-y-auto text-sm shadow-xl duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          'p-6',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="tertiary"
                className="absolute top-4 end-4"
                size="icon-sm"
                icon={<XIcon />}
                aria-label="Close"
              />
            }
          />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({className, ...props}: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex shrink-0 flex-col gap-2', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex shrink-0 flex-row gap-3', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="secondary" size="stretch" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({className, ...props}: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-semibold leading-none text-fg', className)}
      {...props}
    />
  );
}

function DialogDescription({className, ...props}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'whitespace-pre-line text-sm text-fg-tertiary *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-fg',
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
};

export type {DialogOutsidePress};
